// The PRAYOG-QP API: typed calls, error handling (limits), uploads and live job progress.

export type Limits = Record<string, boolean | number | string>;

export interface Usage {
  projects: number;
  files: number;
  storage_mb: number;
  jobs_active: number;
  jobs_today: number;
  cpu_hours_month: number;
}

export interface Me {
  id: number;
  email: string;
  name: string;
  role: "user" | "group_lead" | "admin";
  status: "pending" | "active" | "disabled";
  plan: string;
  plan_name: string;
  overrides: Limits;
  limits: Limits;
  usage: Usage;
  unread_notifications: number;
  created_at: string;
  last_login: string | null;
  organization?: string;      // from the registration form (Keycloak): "Organization(s) affiliated with"
  country?: string;
}

export interface LimitSpec {
  key: string;
  label: string;
  kind: "bool" | "int" | "float" | "choice";
  unit: string;
  help: string;
  choices: string[];
  product: string;
  fallback: boolean | number | string;
  plans: Record<string, boolean | number | string>;
}

export interface Registry {
  limits: LimitSpec[];
  products: { key: string; name: string }[];
  job_kinds: { key: string; product: string; label: string }[];
}

export interface Project {
  id: string;
  product: string;
  name: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  file_count?: number;          // in the project list
}

export interface StoredFile {
  id: string;
  project_id: string | null;
  job_id: string | null;
  kind: "input" | "result" | "copy";   // "copy": of another project's files (a DFT run imported into SKFitter)
  name: string;
  size: number;
  content_type: string;
  status: "pending" | "ready";
  created_at: string;
  expires_at?: string;
}

export type JobStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";

export interface Job {
  id: string;
  kind: string;
  product: string;
  project_id: string | null;
  status: JobStatus;
  progress: number;
  message: string;
  metric: number | null;
  error: string;
  queue: string;
  time_limit_s: number;
  cpu_seconds: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  owner_id: number;
  params?: Record<string, unknown>;
  result?: Record<string, any> | null;
  log?: string;
  queue_position?: number | null;     // a queued job of a one-at-a-time queue (Quantum ESPRESSO): 1 = next
}

export interface ProjectDetail extends Project {
  files: StoredFile[];
  jobs: Job[];
  limits?: Limits;                  // an illustrative project run with Advanced resources: the limits for its work
  advanced_resources?: boolean;
}

export interface AccessRequest {
  id: number;
  user_id: number;
  user_email: string | null;
  limit_key: string;
  requested_value: string | null;
  message: string;
  status: "open" | "approved" | "rejected";
  created_at: string;
  decided_at: string | null;
  decision_note: string;
}

export interface Notice {
  id: number;
  kind: string;
  title: string;
  body: string;
  created_at: string;
  read: boolean;
}

export interface Plan {
  key: string;
  name: string;
  description: string;
  limits: Limits;
  users?: number;
}

export interface AdminUser extends Omit<Me, "limits" | "unread_notifications"> {
  limits?: Limits;
  group: string;
}

export const ACTIVE: JobStatus[] = ["queued", "running"];

/** An error from the API. A refused request because of a plan limit carries ``limit`` (the limit key). */
export class ApiError extends Error {
  status: number;
  limit?: string;
  allowed?: unknown;
  requested?: unknown;

  constructor(status: number, body: any) {
    super(ApiError.message(status, body));
    this.status = status;
    if (body && typeof body === "object" && body.limit) {
      this.limit = body.limit;
      this.allowed = body.allowed;
      this.requested = body.requested;
    }
  }

  static message(status: number, body: any): string {
    const d = body && typeof body === "object" ? body.detail : body;
    if (typeof d === "string" && d) return d;
    if (Array.isArray(d) && d.length) {
      // FastAPI validation errors
      return d.map((e: any) => `${(e.loc || []).filter((x: any) => x !== "body").join(".")}: ${e.msg}`).join("; ");
    }
    if (status === 0) return "The server cannot be reached. Check your connection.";
    return `Request failed (${status}).`;
  }

  get isLimit(): boolean {
    return this.status === 403 && !!this.limit;
  }
}

type TokenSource = () => Promise<string | null>;
let tokenSource: TokenSource = async () => null;
let onUnauthorized: () => void = () => {};

export function configureApi(source: TokenSource, unauthorized: () => void) {
  tokenSource = source;
  onUnauthorized = unauthorized;
}

export const API = "/api";

async function headers(extra?: Record<string, string>): Promise<Record<string, string>> {
  const token = await tokenSource();
  return { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(extra ?? {}) };
}

async function parse(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function request<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(API + path, {
      method,
      headers: await headers(body === undefined ? undefined : { "Content-Type": "application/json" }),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, null);
  }
  const data = await parse(res);
  if (res.status === 401) onUnauthorized();
  if (!res.ok) throw new ApiError(res.status, data);
  return data as T;
}

export const get = <T = any>(path: string) => request<T>("GET", path);
export const post = <T = any>(path: string, body?: unknown) => request<T>("POST", path, body ?? {});
export const patch = <T = any>(path: string, body: unknown) => request<T>("PATCH", path, body);
export const put = <T = any>(path: string, body: unknown) => request<T>("PUT", path, body);
export const del = <T = any>(path: string) => request<T>("DELETE", path);

/** The contents of a file the API serves (or redirects to), e.g. to show a stored plot. */
export async function fileBlob(path: string): Promise<Blob> {
  const res = await fetch(API + path, { headers: await headers() });
  if (!res.ok) throw new ApiError(res.status, await parse(res));
  return res.blob();
}

/** Download a file the API serves (or redirects to): saves it under ``filename``. */
export async function download(path: string, filename: string) {
  const res = await fetch(API + path, { headers: await headers() });
  if (!res.ok) throw new ApiError(res.status, await parse(res));
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// -- uploads: reserve (limits are checked) -> send to storage -> complete ----------------------------------------
export interface UploadLink {
  method: "PUT" | "POST";
  url: string;
  headers?: Record<string, string>;
  fields?: Record<string, string>;
}

export function sendToStorage(link: UploadLink, file: Blob, onProgress?: (fraction: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(link.method, link.url);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else {
        let body: any = xhr.responseText;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          /* S3 answers XML */
        }
        reject(new ApiError(xhr.status, xhr.status === 413 ? "The file is larger than allowed." : body));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, null));
    if (link.method === "POST") {
      const form = new FormData();
      Object.entries(link.fields ?? {}).forEach(([k, v]) => form.append(k, v));
      form.append("file", file);
      xhr.send(form);
    } else {
      Object.entries(link.headers ?? {}).forEach(([k, v]) => xhr.setRequestHeader(k, v));
      xhr.send(file);
    }
  });
}

export async function uploadFile(projectId: string, file: File, onProgress?: (f: number) => void): Promise<StoredFile> {
  const contentType = file.type || "application/octet-stream";
  const start = await post<{ file: StoredFile; upload: UploadLink }>(`/projects/${projectId}/files`, {
    name: file.name,
    size: file.size,
    content_type: contentType,
  });
  await sendToStorage(start.upload, file, onProgress);
  return post<StoredFile>(`/files/${start.file.id}/complete`);
}

// -- live job progress: Server-Sent Events read with fetch (EventSource cannot send the login token) -------------
export function parseSSE(buffer: string): { events: { event: string; data: string }[]; rest: string } {
  const events: { event: string; data: string }[] = [];
  const blocks = buffer.split(/\r?\n\r?\n/);
  const rest = blocks.pop() ?? "";
  for (const block of blocks) {
    let event = "message";
    const data: string[] = [];
    for (const line of block.split(/\r?\n/)) {
      if (line.startsWith(":")) continue;
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trimStart());
    }
    if (data.length) events.push({ event, data: data.join("\n") });
  }
  return { events, rest };
}

export async function followJob(jobId: string, onUpdate: (job: Job) => void, signal: AbortSignal): Promise<void> {
  const res = await fetch(`${API}/jobs/${jobId}/events`, { headers: await headers(), signal });
  if (!res.ok || !res.body) throw new ApiError(res.status, await parse(res));
  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += value;
    const { events, rest } = parseSSE(buffer);
    buffer = rest;
    for (const e of events) if (e.event === "job") onUpdate(JSON.parse(e.data));
  }
}

// -- formatting -----------------------------------------------------------------------------------------------
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

export function formatDate(iso: string | null | undefined, withTime = false): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return withTime
    ? d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function formatDuration(seconds: number): string {
  if (seconds > 0 && seconds < 1) return "< 1 s";
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  return `${(seconds / 3600).toFixed(seconds % 3600 === 0 ? 0 : 1)} h`;
}

export function formatLimit(spec: LimitSpec | undefined, value: unknown): string {
  if (value === undefined || value === null) return "-";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (spec?.unit === "s" && typeof value === "number") return formatDuration(value);
  if (spec?.unit === "MB" && typeof value === "number" && value >= 1024)
    return `${(value / 1024).toFixed(value % 1024 === 0 ? 0 : 1)} GB`;
  const num = typeof value === "number" ? value.toLocaleString() : String(value);
  return spec?.unit ? `${num} ${spec.unit}` : num;
}
